import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShoppingBag, Package, CheckCircle2, Truck, RotateCcw,
  ArrowRight, Star, Clock, Search, SlidersHorizontal,
  Bell, Home, Tag, ChevronRight, User, Eye,
} from "lucide-react";

import type { RootState, AppDispatch } from "../../redux/store";
import {
  listenToGuestProductOrders,
  upsertProductOrder,
} from "../../redux/productOrdersSlice";
import appLogo from "../../assets/app/logo.png";

/* ═══════════════════════════════════════════════
   TOKENS — PEEKABEBI: logo orange→red + cream/peach
   (matches CategoryDetails palette)
═══════════════════════════════════════════════ */
const F = {
  pageBg:    "#FFF8F1",
  cardBg:    "#FFFFFF",
  surface:   "#FFF3E6",
  surface2:  "#FFE8D3",
  ink:       "#2B1A12",
  inkSoft:   "#3A2418",
  muted:     "#9C8A7E",
  mutedDim:  "#B79C87",
  orange:    "#FF7A1A",
  red:       "#FF3D57",
  gold:      "#FF7A1A",
  green:     "#1FAA59",
  border:    "rgba(43,26,18,0.10)",
  borderOr:  "rgba(255,122,26,0.35)",
  grad:      "linear-gradient(135deg,#FF9A3D 0%,#FF3D57 100%)",
  gradH:     "linear-gradient(90deg,#FF9A3D 0%,#FF3D57 100%)",
  gradA:     "linear-gradient(90deg,#FFB347 0%,#FF3D57 50%,#FFB347 100%)",
};

const GRAD_H = F.gradH;
const GRAD_A = F.gradA;

/* fallback image whenever a product/variant photo is missing */
const FALLBACK_IMG = appLogo;
const imgOr = (src?: string | null) => src || FALLBACK_IMG;

/* ═══════════════════════════════════════════════
   CSS
═══════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;600;700;800&family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  ::-webkit-scrollbar{width:4px;}
  ::-webkit-scrollbar-thumb{background:rgba(255,122,26,.25);border-radius:99px;}
  input,button{font-family:inherit;cursor:pointer;}

  @keyframes gradSpin{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
  @keyframes pulseDot{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.35;transform:scale(1.5)}}
  @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
  @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
  @keyframes shimmer{0%{background-position:-200% center}100%{background-position:200% center}}

  input::placeholder{color:#C9A98E;}

  /* ── order card ── */
  .oc{
    background:#fff;
    border:1.5px solid rgba(255,122,26,.18);
    border-radius:22px;overflow:hidden;
    transition:box-shadow .22s,border-color .22s,transform .22s;
    animation:fadeUp .3s ease both;
    position:relative;
  }
  .oc::before{
    content:'';position:absolute;inset:0;border-radius:22px;
    background:linear-gradient(135deg,rgba(255,122,26,.06) 0%,transparent 60%);
    pointer-events:none;
  }
  .oc:hover{
    border-color:rgba(255,61,87,.4);
    box-shadow:0 12px 40px rgba(255,122,26,.18),0 2px 8px rgba(255,61,87,.08);
    transform:translateY(-3px);
  }
  /* card watermark logo — sits behind all card content */
  .oc-watermark{
    position:absolute;right:-18px;bottom:-18px;width:120px;height:120px;
    opacity:.05;pointer-events:none;z-index:0;object-fit:contain;
    transform:rotate(-8deg);
  }

  /* ── filter chips ── */
  .fc{
    display:flex;flex-direction:column;align-items:center;gap:4px;
    padding:10px 14px;border-radius:16px;cursor:pointer;flex-shrink:0;
    border:1.5px solid rgba(255,122,26,.18);background:#fff;
    transition:all .2s;min-width:80px;font-family:'Montserrat',sans-serif;
    outline:none;box-shadow:0 2px 8px rgba(255,122,26,.06);
  }
  .fc:hover{border-color:rgba(255,61,87,.4);background:#FFF3E6;transform:translateY(-1px);}
  .fc.on{
    background:linear-gradient(135deg,#FF9A3D 0%,#FF3D57 100%);
    border-color:transparent;color:#fff;
    box-shadow:0 6px 20px rgba(255,61,87,.25),0 2px 8px rgba(255,122,26,.2);
    transform:translateY(-2px);
  }

  /* ── animated price text ── */
  .pg{
    background:${GRAD_H};background-size:200% 100%;animation:gradSpin 3s ease infinite;
    -webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;
    font-weight:900;letter-spacing:-.5px;font-family:'Baloo 2',sans-serif;
  }

  /* ── buttons ── */
  .btn{display:inline-flex;align-items:center;gap:6px;padding:8px 16px;border-radius:999px;
       font-size:12px;font-weight:700;cursor:pointer;border:none;
       transition:all .18s;font-family:'Montserrat',sans-serif;white-space:nowrap;}
  .btn.gr{
    background:${GRAD_H};background-size:200% 100%;animation:gradSpin 3s ease infinite;
    color:#fff;box-shadow:0 4px 14px rgba(255,61,87,.3);
  }
  .btn.gr:hover{transform:translateY(-1px);box-shadow:0 8px 22px rgba(255,61,87,.42);}
  .btn.ol{background:transparent;color:#2B1A12;border:1.5px solid rgba(255,122,26,.35)!important;}
  .btn.ol:hover{background:#FFF3E6;border-color:rgba(255,61,87,.55)!important;}
  .btn.gn{background:rgba(31,170,89,.08);color:#1FAA59;border:1.5px solid rgba(31,170,89,.28)!important;}
  .btn.rd{background:rgba(255,61,87,.07);color:#FF3D57;border:1.5px solid rgba(255,61,87,.25)!important;}

  /* ── item row ── */
  .ir{display:flex;gap:10px;padding:11px 0;border-bottom:1px solid rgba(255,122,26,.1);cursor:pointer;position:relative;z-index:1;}
  .ir:last-child{border-bottom:none;}
  .ir:hover .ir-name{color:#FF7A1A;}

  /* ── mobile tab ── */
  .tab{display:flex;flex-direction:column;align-items:center;gap:3px;font-size:10px;
       font-weight:700;cursor:pointer;color:#C9A98E;padding:4px 10px;
       transition:color .18s;border:none;background:none;font-family:'Montserrat',sans-serif;}
  .tab.on{color:#FF7A1A;}

  /* ── 3D floating orb ── */
  .orb{
    position:fixed;border-radius:50%;pointer-events:none;z-index:0;
    filter:blur(60px);opacity:.35;
  }

  /* ── page watermark logo (fixed, behind everything) ── */
  .page-watermark{
    position:fixed;top:50%;left:50%;
    width:min(70vw,640px);height:min(70vw,640px);
    transform:translate(-50%,-50%);
    opacity:.035;pointer-events:none;z-index:0;
    object-fit:contain;
  }

  /* ── search bar ── */
  .sw{display:flex;align-items:center;flex:1;background:#FFF3E6;border-radius:14px;
      overflow:hidden;border:1.5px solid rgba(255,122,26,.18);transition:border-color .2s,box-shadow .2s;}
  .sw:focus-within{border-color:rgba(255,61,87,.5);box-shadow:0 0 0 3px rgba(255,61,87,.08);}
`;

/* ─── helpers ─── */
const fmt  = (n?: number) => !n || isNaN(n) ? "0.00" : Number(n).toFixed(2);
const fmtD = (ts: any) => {
  if (!ts) return "—";
  return new Date(typeof ts === "number" ? ts : ts)
    .toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

type SK = "Pending" | "Processing" | "Shipped" | "Delivered";

const SCFG: Record<SK, { bg: string; bd: string; tx: string; Icon: any }> = {
  Pending:    { bg: "rgba(255,122,26,.1)",  bd: "rgba(255,122,26,.35)", tx: "#FF7A1A",  Icon: Clock        },
  Processing: { bg: "rgba(255,61,87,.09)",  bd: "rgba(255,61,87,.3)",   tx: "#FF3D57",  Icon: Package      },
  Shipped:    { bg: "rgba(255,154,61,.14)", bd: "rgba(255,154,61,.4)",  tx: "#FF9A3D",  Icon: Truck        },
  Delivered:  { bg: "rgba(31,170,89,.08)",  bd: "rgba(31,170,89,.3)",   tx: "#1FAA59",  Icon: CheckCircle2 },
};

const getStatus = (items: any[]): SK => {
  if (!items?.length) return "Pending";
  const allDone = items.every(i => i.deliveryStatus?.sellerDelivered && i.deliveryStatus?.buyerReceived);
  const anyShip = items.some(i => i.deliveryStatus?.sellerDelivered);
  const anyConf = items.some(i => i.deliveryStatus);
  if (allDone) return "Delivered";
  if (anyShip) return "Shipped";
  if (anyConf) return "Processing";
  return "Pending";
};

const Badge: React.FC<{ s: SK; sm?: boolean }> = ({ s, sm }) => {
  const c = SCFG[s];
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 4, background: c.bg, border: `1px solid ${c.bd}`, borderRadius: 999, padding: sm ? "3px 9px" : "5px 12px" }}>
      <c.Icon size={sm ? 10 : 11} color={c.tx} strokeWidth={2} />
      <span style={{ fontSize: sm ? 10 : 11, fontWeight: 800, color: c.tx, letterSpacing: .3, fontFamily: "'Montserrat',sans-serif" }}>{s}</span>
    </div>
  );
};

const FilterChip: React.FC<{ icon: React.ElementType; label: string; value: number | string; active: boolean; onClick: () => void }> = ({ icon: Icon, label, value, active, onClick }) => (
  <button className={`fc${active ? " on" : ""}`} onClick={onClick}>
    <Icon size={15} color={active ? "#fff" : F.mutedDim} strokeWidth={1.8} />
    <span style={{ fontSize: 10, fontWeight: 600, color: active ? "#fff" : F.muted, whiteSpace: "nowrap" }}>{label}</span>
    <span style={{ fontSize: 19, fontWeight: 900, color: active ? "#fff" : F.ink, lineHeight: 1, fontFamily: "'Baloo 2',sans-serif" }}>{value}</span>
  </button>
);

/* ── Item row in expanded card ──
   Buy Again lives here (per-item) → navigates to /productdetails/{listingId}
   Review stays gated on `done` (item is fully delivered)
   Falls back to the app logo whenever no product image is available */
const ItemRow: React.FC<{ item: any; order: any; listingsMap: Record<string, any>; dispatch: AppDispatch; onNav: (p: string) => void }> = ({ item, order, listingsMap, dispatch, onNav }) => {
  const p    = listingsMap[item.listingId] ?? listingsMap[item.id] ?? {};
  const vi   = p?.variants?.[item.colorId]?.imageUrl;
  const img  = imgOr(vi || p?.photos?.[0] || item.image);
  const isFallback = img === FALLBACK_IMG;
  const ds   = item.deliveryStatus || {};
  const done = ds.sellerDelivered && ds.buyerReceived; // ← item is delivered
  const ship = ds.sellerDelivered && !done;
  const iSt: SK = done ? "Delivered" : ship ? "Shipped" : "Processing";

  return (
    <div className="ir" onClick={() => onNav(`/metacommercedetails/${item.listingId}`)}>
      <div style={{ width: 52, height: 52, borderRadius: 12, overflow: "hidden", flexShrink: 0, background: F.surface, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <img
          src={img}
          alt={p?.name || item.name || ""}
          style={isFallback
            ? { width: "60%", height: "60%", objectFit: "contain", opacity: 0.55 }
            : { width: "100%", height: "100%", objectFit: "cover" }}
          onError={e => { (e.currentTarget as HTMLImageElement).src = FALLBACK_IMG; (e.currentTarget as HTMLImageElement).style.width = "60%"; (e.currentTarget as HTMLImageElement).style.height = "60%"; (e.currentTarget as HTMLImageElement).style.objectFit = "contain"; (e.currentTarget as HTMLImageElement).style.opacity = "0.55"; }}
        />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="ir-name" style={{ fontSize: 13, fontWeight: 700, color: F.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", transition: "color .15s", fontFamily: "'Baloo 2',sans-serif" }}>{p?.name || item.name}</div>
        <div style={{ display: "flex", gap: 8, marginTop: 3, flexWrap: "wrap" as const }}>
          {item.size   && <span style={{ fontSize: 10, color: F.muted }}>Size: {item.size}</span>}
          {item.colour && <span style={{ fontSize: 10, color: F.muted }}>{item.colour}</span>}
          <span style={{ fontSize: 10, color: F.muted }}>×{item.qty}</span>
        </div>
        <div style={{ marginTop: 6 }}><Badge s={iSt} sm /></div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "space-between", flexShrink: 0, gap: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: F.orange, fontFamily: "'Baloo 2',sans-serif" }}>€{fmt((p?.rate ?? item.rate ?? 0) * item.qty)}</span>
        <div onClick={e => e.stopPropagation()} style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
          {/* Buy Again — per product, always available */}
          <button className="btn ol" style={{ fontSize: 11, padding: "5px 11px" }}
            onClick={() => onNav(`/productdetails/${item.listingId}`)}>
            <RotateCcw size={10} /> Buy Again
          </button>
          {ship && (
            <button className="btn gr" style={{ fontSize: 11, padding: "5px 11px" }}
              onClick={() => dispatch(upsertProductOrder({ paymentId: order.id, listingId: item.listingId, deliveryStatus: { sellerDelivered: true, buyerReceived: true, orderDelivered: true } }))}>
              <CheckCircle2 size={10} /> Received
            </button>
          )}
          {/* Review — only visible once this item is actually delivered */}
          {done && (
            <button className="btn gn" style={{ fontSize: 11, padding: "5px 11px" }}
              onClick={() => onNav(`/productreview/${order.guestId}/${item.listingId}/${order.id}`)}>
              <Star size={10} /> Review
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Order Card ── */
const OrderCard: React.FC<{ order: any; listingsMap: Record<string, any>; guestId: string; dispatch: AppDispatch; onNav: (p: string) => void; delay: number }> = ({ order, listingsMap, guestId, dispatch, onNav, delay }) => {
  const [open, setOpen] = useState(false);
  const fi     = order.items?.[0];
  const prod   = listingsMap[fi?.listingId] ?? listingsMap[fi?.id] ?? {};
  const vi     = prod?.variants?.[fi?.colorId]?.imageUrl;
  const thumb  = imgOr(vi || prod?.photos?.[0] || fi?.image);
  const isFallbackThumb = thumb === FALLBACK_IMG;
  const st     = getStatus(order.items ?? []);
  const cnt    = order.items?.length ?? 0;
  const ordId  = (order.orderId || order.id || "").slice(0, 14).toUpperCase();

  return (
    <motion.div className="oc" layout style={{ marginBottom: 14, animationDelay: `${delay * .05}s` }}>
      {/* card watermark — subtle logo behind all content */}
      <img src={appLogo} alt="" className="oc-watermark" />

      {/* animated gradient top bar */}
      <div style={{ height: 3, background: GRAD_A, backgroundSize: "300% 100%", animation: "gradSpin 4s linear infinite", position: "relative", zIndex: 1 }} />

      {/* header */}
      <div style={{ display: "flex", cursor: "pointer", position: "relative", zIndex: 1 }} onClick={() => setOpen(v => !v)}>
        {/* thumbnail with 3D depth */}
        <div style={{ width: 104, height: 104, flexShrink: 0, overflow: "hidden", position: "relative", background: F.surface, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <img
            src={thumb}
            alt={prod?.name || fi?.name || ""}
            style={isFallbackThumb
              ? { width: "55%", height: "55%", objectFit: "contain", opacity: 0.5 }
              : { width: "100%", height: "100%", objectFit: "cover", transition: "transform .38s" }}
            onMouseEnter={e => { if (!isFallbackThumb) e.currentTarget.style.transform = "scale(1.09)"; }}
            onMouseLeave={e => { if (!isFallbackThumb) e.currentTarget.style.transform = "scale(1)"; }}
            onError={e => { (e.currentTarget as HTMLImageElement).src = FALLBACK_IMG; (e.currentTarget as HTMLImageElement).style.width = "55%"; (e.currentTarget as HTMLImageElement).style.height = "55%"; (e.currentTarget as HTMLImageElement).style.objectFit = "contain"; (e.currentTarget as HTMLImageElement).style.opacity = "0.5"; }}
          />
          {/* 3D glass gradient overlay */}
          {!isFallbackThumb && (
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg,rgba(255,255,255,.15) 0%,transparent 50%,rgba(0,0,0,.12) 100%)", pointerEvents: "none" }} />
          )}
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right,transparent 55%,rgba(255,248,241,.92))", pointerEvents: "none" }} />
        </div>

        {/* info */}
        <div style={{ flex: 1, padding: "13px 16px", display: "flex", flexDirection: "column", justifyContent: "space-between", minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: F.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", letterSpacing: -.2, fontFamily: "'Baloo 2',sans-serif" }}>
                {prod?.name || fi?.name || "Order"}
                {cnt > 1 && <span style={{ fontSize: 11, color: F.muted, fontWeight: 500, marginLeft: 5 }}>+{cnt - 1}</span>}
              </div>
              <div style={{ fontSize: 10, color: F.muted, marginTop: 3, fontFamily: "'Montserrat',sans-serif" }}>
                #{ordId} · {fmtD(order.createdAt)} · {cnt} item{cnt > 1 ? "s" : ""}
              </div>
            </div>
            <Badge s={st} sm />
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 6 }}>
            <span className="pg" style={{ fontSize: 19 }}>€{fmt(order.totalPaid)}</span>
            <ChevronRight size={16} color={F.mutedDim}
              style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform .22s" }} />
          </div>
        </div>
      </div>

      {/* expanded */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: .28, ease: [.4, 0, .2, 1] }}
            style={{ overflow: "hidden", position: "relative", zIndex: 1 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: "16px 18px 20px", borderTop: `1.5px solid ${F.border}`, background: F.surface }}>

              <p style={{ fontSize: 9, fontWeight: 800, letterSpacing: ".2em", color: F.muted, marginBottom: 10, fontFamily: "'Montserrat',sans-serif" }}>ORDER ITEMS</p>
              {(order.items ?? []).map((item: any, i: number) => (
                <ItemRow key={(item.listingId || item.id) + i} item={item} order={order} listingsMap={listingsMap} dispatch={dispatch} onNav={onNav} />
              ))}

              {/* payment summary */}
              <div style={{ marginTop: 16, background: "#fff", border: `1.5px solid ${F.border}`, borderRadius: 16, padding: "14px 16px", boxShadow: "0 4px 16px rgba(255,122,26,.08)" }}>
                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: ".18em", color: F.muted, marginBottom: 10, fontFamily: "'Montserrat',sans-serif" }}>PAYMENT SUMMARY</div>
                {[
                  { l: "Subtotal",    v: `€${fmt(order.subtotal ?? (order.totalPaid - (order.guestsFee ?? 0)))}` },
                  { l: "Service Fee", v: `€${fmt(order.guestsFee)}` },
                  { l: "Delivery",    v: order.deliveryTotal ? `€${fmt(order.deliveryTotal)}` : "Free" },
                ].map(({ l, v }, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${F.border}` }}>
                    <span style={{ fontSize: 12, color: F.muted, fontFamily: "'Montserrat',sans-serif" }}>{l}</span>
                    <span style={{ fontSize: 12, color: F.inkSoft, fontWeight: 600, fontFamily: "'Montserrat',sans-serif" }}>{v}</span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: F.ink, fontFamily: "'Baloo 2',sans-serif" }}>Total Paid</span>
                  <span className="pg" style={{ fontSize: 18 }}>€{fmt(order.totalPaid)}</span>
                </div>
              </div>

              {/* order-level actions */}
              <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" as const }}>
                <button className="btn ol" onClick={() => onNav(`/metacommercedetails/${fi?.listingId}`)}>
                  <Eye size={12} /> View Details
                </button>
                {st === "Shipped" && (
                  <button className="btn ol" onClick={() => onNav(`/track/${order.id}/${fi?.listingId}`)}>
                    <Truck size={12} /> Track Order
                  </button>
                )}
                {(st === "Pending" || st === "Processing") && (
                  <button className="btn rd">Cancel Order</button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════ */
const ClientProductOrders: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const dispatch   = useDispatch<AppDispatch>();
  const navigate   = useNavigate();
  const authUser   = useSelector((s: RootState) => s.auth.user);
  const guestId    = userId ?? authUser?.userId ?? "";

  const orders   = useSelector((s: RootState) => s.productOrders.orders);
  const listings = useSelector((s: RootState) => s.products.products);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | SK>("all"); // "all" = active orders (Delivered excluded)
  const [isMob,  setIsMob]  = useState(window.innerWidth < 768);

  useEffect(() => { const fn = () => setIsMob(window.innerWidth < 768); window.addEventListener("resize", fn); return () => window.removeEventListener("resize", fn); }, []);
  useEffect(() => { if (guestId) dispatch(listenToGuestProductOrders(guestId)); }, [guestId, dispatch]);

  const listingsMap = useMemo(() => {
    const m: Record<string, any> = {};
    listings.forEach((l: any) => { m[l.listingId] = l; });
    return m;
  }, [listings]);

  const sorted = useMemo(() => [...orders].sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0)), [orders]);

  const cnt = (s: SK) => sorted.filter(o => getStatus(o.items ?? []) === s).length;
  const pendingN    = cnt("Pending");
  const processingN = cnt("Processing");
  const shippedN    = cnt("Shipped");
  const deliveredN  = cnt("Delivered");
  const activeN     = pendingN + processingN + shippedN; // "All Orders" chip = active orders only

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sorted.filter(o => {
      const status = getStatus(o.items ?? []);

      if (filter === "all") {
        // Default/"All" view shows only active orders — Delivered lives on its own tab
        if (status === "Delivered") return false;
      } else if (status !== filter) {
        return false;
      }

      if (!q) return true;
      const fi = o.items?.[0]; const p = listingsMap[fi?.listingId] ?? {};
      return `${p?.name ?? ""} ${o.id ?? ""} ${fi?.name ?? ""}`.toLowerCase().includes(q);
    });
  }, [sorted, filter, search, listingsMap]);

  /* ── empty state — no orders at all ── */
  if (sorted.length === 0) return (
    <div style={{ minHeight: "100vh", background: F.pageBg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'Baloo 2',sans-serif", position: "relative", overflow: "hidden" }}>
      <style>{CSS}</style>
      <img src={appLogo} alt="" className="page-watermark" />
      {/* 3D orbs */}
      <div className="orb" style={{ width: 320, height: 320, background: "radial-gradient(circle,#FF7A1A,transparent)", top: -80, right: -80 }} />
      <div className="orb" style={{ width: 240, height: 240, background: "radial-gradient(circle,#FF3D57,transparent)", bottom: -60, left: -60 }} />
      <div style={{ textAlign: "center", padding: 32, position: "relative", zIndex: 1 }}>
        <motion.div animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
          style={{ width: 90, height: 90, borderRadius: "50%", background: F.gradH, backgroundSize: "200% 100%", animation: "gradSpin 3s ease infinite", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", boxShadow: "0 16px 40px rgba(255,122,26,.35),0 4px 12px rgba(255,61,87,.2),inset 0 2px 6px rgba(255,255,255,.25)" }}>
          <ShoppingBag size={36} color="#fff" />
        </motion.div>
        <h2 style={{ fontWeight: 900, fontSize: 26, color: F.ink, marginBottom: 8, letterSpacing: -.3 }}>No Orders Yet</h2>
        <p style={{ color: F.muted, fontSize: 14, lineHeight: 1.7, marginBottom: 28, fontFamily: "'Montserrat',sans-serif" }}>Your curated collection awaits.</p>
        <button className="btn gr" style={{ fontSize: 14, padding: "13px 32px" }} onClick={() => navigate("/ProductsHome")}>
          <ShoppingBag size={14} /> Start Shopping <ArrowRight size={13} />
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: F.pageBg, fontFamily: "'Montserrat',sans-serif", paddingBottom: 80, position: "relative" }}>
      <style>{CSS}</style>

      {/* page background watermark — huge, faint, fixed, behind everything */}
      <img src={appLogo} alt="" className="page-watermark" />

      {/* background orbs */}
      <div className="orb" style={{ width: 400, height: 400, background: "radial-gradient(circle,rgba(255,122,26,.15),transparent)", top: -100, right: -100 }} />
      <div className="orb" style={{ width: 300, height: 300, background: "radial-gradient(circle,rgba(255,61,87,.1),transparent)", bottom: 100, left: -80 }} />

      {/* ── STICKY HEADER ── */}
      <div style={{ background: "rgba(255,255,255,.97)", backdropFilter: "blur(16px)", borderBottom: `1px solid ${F.border}`, position: "sticky", top: 0, zIndex: 100, boxShadow: "0 2px 20px rgba(255,122,26,.08)" }}>
        <div style={{ height: 3, background: GRAD_A, backgroundSize: "300% 100%", animation: "gradSpin 4s linear infinite" }} />

        <div style={{ padding: "16px 20px 12px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 34, height: 34, borderRadius: "50%", overflow: "hidden", flexShrink: 0, border: `1.5px solid ${F.border}`, background: F.surface, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <img src={appLogo} alt="PEEKABEBI" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
              <div>
                <h1 style={{ fontWeight: 900, fontSize: isMob ? 22 : 28, color: F.ink, letterSpacing: -.5, lineHeight: 1.1, fontFamily: "'Baloo 2',sans-serif" }}>My Orders</h1>
                <p style={{ fontSize: 12, color: F.muted, marginTop: 3 }}>Track and manage your orders</p>
              </div>
            </div>
            <div style={{ position: "relative", padding: 4, cursor: "pointer" }}>
              <Bell size={22} color={F.mutedDim} />
              {pendingN > 0 && (
                <div style={{ position: "absolute", top: 0, right: 0, width: 17, height: 17, borderRadius: "50%", background: GRAD_H, backgroundSize: "200% 100%", animation: "gradSpin 3s ease infinite", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(255,61,87,.35)" }}>
                  <span style={{ fontSize: 9, fontWeight: 900, color: "#fff" }}>{pendingN}</span>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <div className="sw" style={{ flex: 1 }}>
              <div style={{ padding: "0 13px" }}><Search size={14} color={F.mutedDim} /></div>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search orders…"
                style={{ flex: 1, background: "none", border: "none", outline: "none", fontSize: 13, color: F.ink, padding: "10px 4px" }} />
            </div>
            <button style={{ display: "flex", alignItems: "center", gap: 6, background: F.surface, border: `1.5px solid ${F.border}`, borderRadius: 14, padding: "10px 16px", color: F.inkSoft, fontWeight: 700, fontSize: 12, cursor: "pointer", flexShrink: 0 }}>
              <SlidersHorizontal size={13} color={F.muted} /> Filter
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, overflowX: "auto", scrollbarWidth: "none" as any, padding: "0 20px 14px" }}>
          <FilterChip icon={ShoppingBag}  label="All Orders"  value={activeN}     active={filter === "all"}        onClick={() => setFilter("all")} />
          <FilterChip icon={Clock}        label="Pending"     value={pendingN}    active={filter === "Pending"}    onClick={() => setFilter("Pending")} />
          <FilterChip icon={Package}      label="Processing"  value={processingN} active={filter === "Processing"} onClick={() => setFilter("Processing")} />
          <FilterChip icon={Truck}        label="Shipped"     value={shippedN}    active={filter === "Shipped"}    onClick={() => setFilter("Shipped")} />
          <FilterChip icon={CheckCircle2} label="Delivered"   value={deliveredN}  active={filter === "Delivered"}  onClick={() => setFilter("Delivered")} />
        </div>
      </div>

      {/* ── ORDER LIST ── */}
      <div style={{ maxWidth: isMob ? "100%" : 680, margin: "0 auto", padding: isMob ? "16px 14px" : "24px 20px", position: "relative", zIndex: 1 }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", paddingTop: 60 }}>
            <div style={{ fontSize: 52, marginBottom: 14, animation: "float 3s ease-in-out infinite" }}>🛍️</div>
            <h3 style={{ fontWeight: 900, fontSize: 18, color: F.ink, marginBottom: 8, fontFamily: "'Baloo 2',sans-serif" }}>No orders found</h3>
            <p style={{ color: F.muted, fontSize: 13, marginBottom: 22 }}>Try adjusting your filter or search.</p>
            <button className="btn gr" style={{ fontSize: 13, padding: "11px 28px" }} onClick={() => navigate("/ProductsHome")}>
              <ShoppingBag size={13} /> Start Shopping <ArrowRight size={12} />
            </button>
          </div>
        ) : filtered.map((order, i) => (
          <OrderCard key={order.id} order={order} listingsMap={listingsMap} guestId={guestId}
            dispatch={dispatch} onNav={path => navigate(path)} delay={i} />
        ))}
      </div>

      {/* ── MOBILE BOTTOM TABS ── */}
      {isMob && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, height: 64, background: "rgba(255,255,255,.97)", display: "flex", justifyContent: "space-around", alignItems: "center", boxShadow: "0 -4px 24px rgba(255,122,26,.12)", zIndex: 100, borderTop: `1px solid ${F.border}`, backdropFilter: "blur(12px)" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: GRAD_A, backgroundSize: "300% 100%", animation: "gradSpin 4s linear infinite" }} />
          {[
            { icon: <Home size={21} />,        label: "Home",    path: `/ProductsHome/${guestId}`,        active: false },
            { icon: <Tag size={21} />,          label: "Deals",   path: "/deals",                          active: false },
            { icon: <ShoppingBag size={21} />,  label: "Orders",  path: `/ClientProductOrders/${guestId}`, active: true  },
            { icon: <User size={21} />,         label: "Profile", path: `/profile/${guestId}`,             active: false },
          ].map(t => (
            <motion.button key={t.label} whileTap={{ scale: .88 }}
              className={`tab${t.active ? " on" : ""}`}
              onClick={() => navigate(t.path)}>
              {React.cloneElement(t.icon as any, { color: t.active ? F.orange : F.mutedDim })}
              <span>{t.label}</span>
            </motion.button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientProductOrders;